import React, { useMemo } from 'react';
import { usePublishCourseDraft } from '@edusense/course-client';
import type { CourseDraftDetailResponse } from '@edusense/course-client';
import { CheckCircle, XCircle, Loader2, Send } from 'lucide-react';

export interface PublishTabContentProps {
  courseId: string;
  courseData?: CourseDraftDetailResponse;
  disabled?: boolean;
  onPublishSuccess?: () => void;
}

interface PublishCheckItem {
  id: string;
  label: string;
  passed: boolean;
  description?: string;
}

export const PublishTabContent: React.FC<PublishTabContentProps> = ({
  courseId,
  courseData,
  disabled = false,
  onPublishSuccess,
}) => {
  const publishMutation = usePublishCourseDraft({
    mutation: {
      onSuccess: () => {
        onPublishSuccess?.();
      },
    },
  });

  // 게시 조건 체크
  const publishChecks = useMemo<PublishCheckItem[]>(() => {
    const hasTitle = !!courseData?.title?.trim();
    const hasDescription = !!courseData?.description?.trim();
    const hasLevel = !!courseData?.level;

    // ✅ API는 최상위에 sections를 반환 (curriculum.sections가 아님)
    const sections = courseData?.sections || [];
    const hasCurriculum = sections.length > 0;
    const hasLessons = sections.some(section =>
      (section.lectures || []).length > 0
    );

    // ✅ API는 price.amount를 반환 (pricing.priceAmount가 아님)
    const hasPrice = typeof courseData?.price?.amount === 'number';
    const isFree = courseData?.price?.amount === 0;
    const hasPricing = isFree || (hasPrice && (courseData?.price?.amount ?? 0) > 0);

    return [
      {
        id: 'basic-info',
        label: '기본 정보 완료',
        passed: hasTitle && hasDescription && hasLevel,
        description: '제목, 설명, 난이도가 모두 입력되어야 합니다.',
      },
      {
        id: 'curriculum',
        label: '커리큘럼 완료',
        passed: hasCurriculum && hasLessons,
        description: '최소 1개 이상의 섹션과 레슨이 필요합니다.',
      },
      {
        id: 'pricing',
        label: '가격 설정 완료',
        passed: hasPricing,
        description: '무료 강의이거나 가격이 설정되어야 합니다.',
      },
    ];
  }, [courseData]);

  const allChecksPassed = publishChecks.every(check => check.passed);
  const isPublished = courseData?.isPublished === true;

  const handlePublish = async () => {
    if (!allChecksPassed || isPublished || disabled || courseId === 'new') {
      return;
    }

    try {
      await publishMutation.mutateAsync({ id: courseId });
    } catch (error) {
      console.error('게시 실패:', error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold mb-4">강의 게시</h2>

        {isPublished && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle size={20} />
              <span className="font-medium">이 강의는 이미 게시되었습니다.</span>
            </div>
          </div>
        )}

        <div className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-700">게시 조건</h3>

          {publishChecks.map((check) => (
            <div
              key={check.id}
              className={`p-4 rounded-lg border ${
                check.passed
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start gap-3">
                {check.passed ? (
                  <CheckCircle className="text-green-600 mt-0.5" size={20} />
                ) : (
                  <XCircle className="text-gray-400 mt-0.5" size={20} />
                )}
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{check.label}</div>
                  {check.description && (
                    <div className="text-sm text-gray-600 mt-1">
                      {check.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {publishMutation.isError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <XCircle size={20} />
              <span className="font-medium">
                게시 중 오류가 발생했습니다. 다시 시도해주세요.
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handlePublish}
            disabled={!allChecksPassed || isPublished || disabled || publishMutation.isPending}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-lg font-medium
              transition-all duration-200
              ${
                !allChecksPassed || isPublished || disabled || publishMutation.isPending
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
              }
            `}
          >
            {publishMutation.isPending ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>게시 중...</span>
              </>
            ) : (
              <>
                <Send size={20} />
                <span>{isPublished ? '게시됨' : '게시하기'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishTabContent;
